import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Chip,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DashboardLayout from '../components/DashboardLayout';
import * as XLSX from 'xlsx';
import { getContacts, deleteContact } from '../services/contactService';
import AdminEmptyState from '../components/AdminEmptyState';
import AdminPageHeader from '../components/AdminPageHeader';
import AdminPagination from '../components/AdminPagination';
import AdminSection from '../components/AdminSection';
import useAdminPagination from '../hooks/useAdminPagination';

function ContactsPage() {
  const [contacts, setContacts] = useState([]);
  const [error, setError] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const {
    page,
    rowsPerPage,
    count,
    paginatedItems: paginatedContacts,
    handlePageChange,
    handleRowsPerPageChange,
  } = useAdminPagination(contacts, 10);

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      const data = await getContacts();
      setContacts(Array.isArray(data) ? data : []);
      setError('');
    } catch (loadError) {
      console.error('Error fetching contacts:', loadError);
      setContacts([]);
      setError('Failed to load contacts.');
    }
  };

  const handleDelete = async (contactId) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) {
      return;
    }

    try {
      await deleteContact(contactId);
      setContacts((current) => current.filter((contact) => contact.contactId !== contactId));
    } catch (deleteError) {
      console.error('Error deleting contact:', deleteError);
      setError('Failed to delete contact.');
    }
  };

  const handleExportToExcel = () => {
    const exportData = contacts.map((contact) => ({
      Name: contact.name,
      Company: contact.company,
      Email: contact.email,
      Message: contact.message,
      'Date Sent': contact.submittedAt ? new Date(contact.submittedAt).toLocaleString() : 'N/A',
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Contacts');
    XLSX.writeFile(workbook, 'Contacts.xlsx');
  };

  return (
    <DashboardLayout>
      <Stack spacing={3}>
        <AdminPageHeader
          eyebrow="Operations"
          title="Contacts"
          description="Review inbound contact requests and export them when you need an offline handoff."
          meta={<Chip label={`${contacts.length} submissions`} variant="outlined" />}
          action={
            <Button variant="outlined" onClick={handleExportToExcel} disabled={!contacts.length}>
              Export to Excel
            </Button>
          }
        />

        {error ? <Alert severity="error">{error}</Alert> : null}

        {isMobile ? (
          contacts.length ? (
            <Grid container spacing={2}>
              {paginatedContacts.map((contact) => (
                <Grid item xs={12} key={contact.contactId}>
                  <AdminSection>
                    <Stack spacing={1}>
                      <Typography variant="h6">{contact.name}</Typography>
                      <Typography color="text.secondary">{contact.email}</Typography>
                      <Typography color="text.secondary">{contact.company || 'No company'}</Typography>
                      <Typography>{contact.message || 'No message'}</Typography>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" color="text.secondary">
                          {contact.submittedAt ? new Date(contact.submittedAt).toLocaleString() : 'N/A'}
                        </Typography>
                        <IconButton color="error" onClick={() => handleDelete(contact.contactId)}>
                          <DeleteIcon />
                        </IconButton>
                      </Stack>
                    </Stack>
                  </AdminSection>
                </Grid>
              ))}
            </Grid>
          ) : (
            <AdminSection>
              <AdminEmptyState
                title="No contacts yet"
                description="New contact submissions will appear here once users start reaching out."
              />
            </AdminSection>
          )
        ) : (
          <AdminSection sx={{ p: 0, overflow: 'hidden' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Company</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Message</TableCell>
                  <TableCell>Date Sent</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {contacts.length ? (
                  paginatedContacts.map((contact) => (
                    <TableRow key={contact.contactId}>
                      <TableCell sx={{ fontWeight: 600 }}>{contact.name}</TableCell>
                      <TableCell>{contact.company || '-'}</TableCell>
                      <TableCell>{contact.email || '-'}</TableCell>
                      <TableCell sx={{ maxWidth: 320 }}>
                        <Typography noWrap color="text.secondary">
                          {contact.message || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {contact.submittedAt ? new Date(contact.submittedAt).toLocaleString() : 'N/A'}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton color="error" onClick={() => handleDelete(contact.contactId)}>
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <AdminEmptyState
                        title="No contacts yet"
                        description="New contact submissions will appear here once users start reaching out."
                      />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <AdminPagination
              count={count}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          </AdminSection>
        )}
      </Stack>
    </DashboardLayout>
  );
}

export default ContactsPage;
